import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, X } from 'lucide-react';
import FavouriteButton from '../components/FavouriteButton';
import SEOHead, { imageGallerySchema, breadcrumbSchema } from '../components/SEOHead';
import { trackView } from '../utils/viewTracker';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CollectionPage = () => {
  const { id } = useParams();
  const [collection, setCollection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  // Track view for admin analytics (server de-dupes by IP within 30min)
  useEffect(() => {
    if (id) trackView('collection', id);
  }, [id]);

  useEffect(() => {
    const fetchCollection = async () => {
      try {
        const res = await fetch(`${API_URL}/api/content/collections`);
        const collections = await res.json();
        
        const found = collections.find(c => c.id === id);
        
        if (found) {
          const images = [];
          
          if (found.images && found.images.length > 0) {
            found.images.forEach((img, idx) => {
              const imageData = typeof img === 'string' 
                ? { url: img, order: idx }
                : { url: img.url || img, order: img.order ?? idx };
              
              images.push({
                id: `${found.id}-${idx}`,
                src: imageData.url,
                order: imageData.order
              });
            });
          }
          
          images.sort((a, b) => a.order - b.order);
          
          setCollection({
            ...found,
            allImages: images,
            coverFocalPoint: found.cover_focal_point || { x: 50, y: 50 }
          });
        }
      } catch (error) {
        console.error('Error fetching collection:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCollection();
  }, [id]);

  // Lightbox navigation
  const openLightbox = (image, index) => {
    setLightboxImage(image);
    setLightboxIndex(index);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    document.body.style.overflow = 'unset';
  };

  const nextImage = () => {
    if (collection?.allImages) {
      const nextIndex = (lightboxIndex + 1) % collection.allImages.length;
      setLightboxIndex(nextIndex);
      setLightboxImage(collection.allImages[nextIndex]);
    }
  };

  const prevImage = () => {
    if (collection?.allImages) {
      const prevIndex = (lightboxIndex - 1 + collection.allImages.length) % collection.allImages.length;
      setLightboxIndex(prevIndex);
      setLightboxImage(collection.allImages[prevIndex]);
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxImage) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxImage, lightboxIndex]);

  if (loading) {
    return (
      <main className="bg-white min-h-screen flex items-center justify-center">
        <div className="text-black/30 tracking-widest text-sm uppercase">Loading...</div>
      </main>
    );
  }

  if (!collection) {
    return (
      <main className="bg-white min-h-screen pt-32">
        <div className="max-w-7xl mx-auto px-8 lg:px-16 text-center py-24">
          <h1 className="font-presti text-3xl text-black/80 mb-6">Collection Not Found</h1>
          <Link 
            to="/photo" 
            className="inline-flex items-center gap-3 text-xs tracking-[0.2em] uppercase text-black/50 hover:text-black transition-colors"
          >
            <ArrowLeft size={14} />
            <span>Back to Gallery</span>
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-white min-h-screen">
      <SEOHead
        title={`${collection.couple} — ${collection.location || 'Wedding'} Wedding Photos`}
        description={`Luxury wedding photography for ${collection.couple}${collection.location ? ` in ${collection.location}` : ''} by Crystal Media. ${collection.allImages?.length || 0} editorial images${collection.tags?.length ? ` · ${collection.tags.join(', ')}` : ''}.`}
        url={`/collection/${collection.id}`}
        image={collection.cover_image_url}
        type="article"
        keywords={[`${collection.location} wedding photographer`, ...(collection.tags || []).map(t => `${t} wedding photography`)]}
        jsonLd={[
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Collections', path: '/collections' }, { name: collection.couple, path: `/collection/${collection.id}` }]),
          imageGallerySchema(collection, (collection.allImages || []).map(i => i.src)),
        ]}
      />
      {/* Clean Minimal Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-sm">
        <nav className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Back Button */}
            <Link 
              to="/photo"
              className="flex items-center gap-2 text-black/50 hover:text-black transition-colors"
            >
              <ArrowLeft size={18} strokeWidth={1.5} />
              <span className="text-xs tracking-[0.15em] uppercase">Back</span>
            </Link>
            
            {/* Collection Title - Center */}
            <div className="absolute left-1/2 -translate-x-1/2 text-center">
              <h1 className="font-presti text-lg md:text-xl text-black/80">
                {collection.couple}
              </h1>
            </div>
            
            {/* Photo Count */}
            <div className="text-xs tracking-[0.15em] uppercase text-black/40">
              {collection.allImages?.length || 0} Photos
            </div>
          </div>
        </nav>
        <div className="h-px bg-black/5" />
      </header>

      {/* Gallery Grid - Clean Masonry */}
      <section className="pt-24 pb-16 px-4 md:px-8 lg:px-12">
        <div className="max-w-[1800px] mx-auto">
          {collection.allImages && collection.allImages.length > 0 ? (
            <div className="columns-2 md:columns-3 lg:columns-4 gap-3 md:gap-4">
              {collection.allImages.map((image, index) => (
                <div
                  key={image.id}
                  className="break-inside-avoid mb-3 md:mb-4 cursor-pointer group"
                  onClick={() => openLightbox(image, index)}
                >
                  <div className="relative overflow-hidden bg-gray-100">
                    <img
                      src={image.src}
                      alt={`${collection.couple} wedding photography${collection.location ? ` in ${collection.location}` : ''} — image ${index + 1} by Crystal Media`}
                      className="w-full h-auto transition-transform duration-500 group-hover:scale-[1.02]"
                      loading={index < 6 ? 'eager' : 'lazy'}
                      decoding="async"
                      fetchPriority={index < 2 ? 'high' : 'auto'}
                    />
                    {/* Heart — opacity ramps on hover (desktop) / always-on (touch) */}
                    <div className="absolute top-2 right-2 sm:opacity-0 group-hover:opacity-100 transition-opacity duration-200 opacity-100 sm:opacity-0">
                      <FavouriteButton itemType="photo" itemId={image.id} variant="light" size={15} />
                    </div>
                    {/* Subtle hover overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 pointer-events-none" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-24">
              <p className="text-black/30 tracking-widest text-sm uppercase">No photos in this collection yet</p>
            </div>
          )}
        </div>
      </section>

      {/* Simple Footer Navigation */}
      <section className="py-12 px-6 border-t border-black/5">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link
            to="/photo"
            className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-black/40 hover:text-black transition-colors"
          >
            <ArrowLeft size={14} />
            <span>Gallery</span>
          </Link>
          
          <p className="text-[10px] tracking-[0.2em] uppercase text-black/30">
            {collection.location}
          </p>
          
          <Link
            to="/inquire"
            className="text-xs tracking-[0.15em] uppercase text-black/40 hover:text-black transition-colors"
          >
            Inquire
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-[100] bg-white flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close Button */}
          <button 
            className="absolute top-6 right-6 p-2 text-black/40 hover:text-black transition-colors z-10"
            onClick={closeLightbox}
          >
            <X size={24} strokeWidth={1.5} />
          </button>
          
          {/* Navigation */}
          <button 
            className="absolute left-6 top-1/2 -translate-y-1/2 p-3 text-black/30 hover:text-black transition-colors"
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
          >
            <ArrowLeft size={28} strokeWidth={1} />
          </button>
          
          <button 
            className="absolute right-6 top-1/2 -translate-y-1/2 p-3 text-black/30 hover:text-black transition-colors rotate-180"
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
          >
            <ArrowLeft size={28} strokeWidth={1} />
          </button>
          
          {/* Image */}
          <div 
            className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage.src}
              alt={`${collection.couple} - Photo ${lightboxIndex + 1}`}
              className="max-w-full max-h-[85vh] object-contain"
            />
          </div>
          
          {/* Counter */}
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-xs tracking-[0.2em] text-black/40">
            {lightboxIndex + 1} / {collection.allImages?.length}
          </div>
        </div>
      )}
    </main>
  );
};

export default CollectionPage;
